// The marker-first / lazy-mint question form behind `?label=new` (+
// `?marker=`), rendered at the top of the results rail rather than in a
// layer so the conversation stays interactive: choose an existing Marker or
// mint a human-only one, answer it, pick its evidence in the conversation
// (an utterance range or a text selection), and save. Labels on an existing
// Mark are written in the expanded rail row instead.

import { useEffect, useId, useRef } from "react";

import type { components } from "../../api/schema";
import {
  Badge,
  Button,
  Inline,
  InlineError,
  Section,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import type { Assertion, MarkerOutput } from "../markerAuthoringDraft";
import {
  assertionSuggestions,
  locatorLabel,
  wholeTranscriptLocator,
  type InspectorLocalDraft,
  type LabelDraft,
} from "./labelInspectorDraft";
import { AnswerControl } from "./labelInspectorControls";

type Marker = components["schemas"]["MarkerResponse"];

export const NEW_QUESTION = "new-question";

export function LabelInspector({
  assertion,
  isLoading,
  isSaving,
  local,
  markers,
  output,
  onClose,
  onCriterionChange,
  onDraftChange,
  onNewOutputChange,
  onDisplayNameChange,
  onRetry,
  onSave,
}: {
  assertion: Assertion | null;
  isLoading: boolean;
  isSaving: boolean;
  local: InspectorLocalDraft;
  markers: Marker[];
  output: MarkerOutput;
  onClose: () => void;
  onCriterionChange: (criterion: string) => void;
  onDraftChange: (patch: Partial<LabelDraft>) => void;
  onNewOutputChange: (output: MarkerOutput) => void;
  onDisplayNameChange: (displayName: string) => void;
  onRetry: () => void;
  onSave: () => void;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const suggestionId = useId();
  useEffect(() => {
    if (!isLoading) {
      formRef.current?.focus();
    }
  }, [local.seedKey, isLoading]);
  const suggestions = assertionSuggestions(assertion);
  const hasCriterion = local.criterion.length > 0;

  return (
    <form
      aria-label="Add a question"
      ref={formRef}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !isSaving) {
          event.preventDefault();
          onClose();
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!isSaving && !isLoading) {
          if (local.validationError) {
            onRetry();
          } else {
            onSave();
          }
        }
      }}
    >
      <Stack gap={5}>
        {isLoading ? (
          <Skeleton label="Loading Markers" shape="detail-field" />
        ) : (
          <Section heading="Criterion">
            <Stack gap={3}>
              <Select
                label="Marker"
                value={local.criterion}
                onChange={(event) => {
                  onCriterionChange(event.currentTarget.value);
                }}
              >
                <option value="">Select a Marker</option>
                {markers.map((marker) => (
                  <option key={marker.id} value={marker.id}>
                    {marker.current_revision.display_name}
                  </option>
                ))}
                <option value={NEW_QUESTION}>New question…</option>
              </Select>
              {local.criterion === NEW_QUESTION ? (
                <>
                  <TextInput
                    label="Display name"
                    value={local.displayName}
                    onChange={(event) => {
                      onDisplayNameChange(event.currentTarget.value);
                    }}
                  />
                  <Select
                    label="Output shape"
                    value={local.newOutput}
                    onChange={(event) => {
                      onNewOutputChange(
                        event.currentTarget.value as MarkerOutput,
                      );
                    }}
                  >
                    <option value="boolean">Boolean</option>
                    <option value="number">Number</option>
                    <option value="category">Category</option>
                  </Select>
                </>
              ) : null}
            </Stack>
          </Section>
        )}
        {!isLoading && hasCriterion ? (
          <>
            <Section heading="Your answer">
              <AnswerControl
                draft={local.draft}
                output={output}
                suggestionId={suggestionId}
                suggestions={suggestions}
                onChange={onDraftChange}
              />
            </Section>
            <Section heading="Where">
              <Stack gap={2}>
                <Inline gap={2}>
                  <Badge>{locatorLabel(local.draft.locator)}</Badge>
                  {local.draft.locator.kind !== "whole_transcript" ? (
                    <Button
                      size="sm"
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        onDraftChange({ locator: wholeTranscriptLocator });
                      }}
                    >
                      Clear
                    </Button>
                  ) : null}
                </Inline>
                <Text variant="muted">
                  Click an utterance in the conversation, then another, to pick
                  the range this answer is about.
                </Text>
              </Stack>
            </Section>
            <Select
              label="Flag"
              value={local.draft.flaggedReason ?? "none"}
              onChange={(event) => {
                onDraftChange({
                  flaggedReason:
                    event.currentTarget.value === "none"
                      ? null
                      : (event.currentTarget
                          .value as LabelDraft["flaggedReason"]),
                });
              }}
            >
              <option value="low_quality">Low quality</option>
              <option value="ambiguous">Ambiguous</option>
              <option value="off_topic">Off topic</option>
              <option value="none">None</option>
            </Select>
          </>
        ) : null}
        {!isLoading && local.validationError ? (
          <InlineError>{local.validationError}</InlineError>
        ) : null}
        {!isLoading && hasCriterion ? (
          <Inline>
            <Button disabled={isSaving} type="submit">
              {isSaving
                ? "Saving..."
                : local.validationError
                  ? "Retry"
                  : "Save label"}
            </Button>
            <Button
              disabled={isSaving}
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </Inline>
        ) : null}
      </Stack>
    </form>
  );
}
